import axios from 'axios';
import { Plot, PlotFormData, PlotSummary, PlotSize, BatchCreatePlotsData } from '@/types/plot';

const API_BASE_URL = `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api`;

class PlotService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }

  // Get all plots for a specific asset
  async getAssetPlots(assetId: string, filters?: {
    section?: string;
    row?: string;
    plotSize?: PlotSize;
    isAvailable?: boolean;
    status?: string;
  }) {
    try {
      const params = new URLSearchParams();
      if (filters?.section) params.append('section', filters.section);
      if (filters?.row) params.append('row', filters.row);
      if (filters?.plotSize) params.append('plotSize', filters.plotSize);
      if (filters?.isAvailable !== undefined) params.append('isAvailable', filters.isAvailable.toString());
      if (filters?.status) params.append('status', filters.status);

      const response = await axios.get(
        `${API_BASE_URL}/provider-dashboard/assets/${assetId}/plots?${params.toString()}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching asset plots:', error);
      throw error;
    }
  }

  // Get plot summary for an asset
  async getAssetPlotSummary(assetId: string) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/provider-dashboard/assets/${assetId}/plots/summary`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching plot summary:', error);
      throw error;
    }
  }

  // Get a specific plot by ID
  async getPlotById(plotId: string) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/plots/${plotId}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching plot:', error);
      throw error;
    }
  }

  // Create a new plot for an asset
  async createPlot(assetId: string, plotData: PlotFormData) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/provider-dashboard/assets/${assetId}/plots`,
        plotData,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error creating plot:', error);
      throw error;
    }
  }

  // Create multiple plots at once (batch creation)
  async batchCreatePlots(assetId: string, batchData: Omit<BatchCreatePlotsData, 'assetId'>) {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/provider-dashboard/assets/${assetId}/plots/batch`,
        batchData,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error batch creating plots:', error);
      throw error;
    }
  }

  // Update an existing plot
  async updatePlot(plotId: string, updateData: Partial<PlotFormData>) {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/provider-dashboard/plots/${plotId}`,
        updateData,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating plot:', error);
      throw error;
    }
  }

  // Update plot availability
  async updatePlotAvailability(plotId: string, isAvailable: boolean, status?: string) {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/provider-dashboard/plots/${plotId}/availability`,
        { isAvailable, status },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating plot availability:', error);
      throw error;
    }
  }

  // Delete a plot
  async deletePlot(plotId: string) {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/provider-dashboard/plots/${plotId}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting plot:', error);
      throw error;
    }
  }

  // Get standard dimensions for plot size
  async getPlotDimensions(plotSize: PlotSize) {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/provider-dashboard/plots/dimensions/${plotSize}`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching plot dimensions:', error);
      throw error;
    }
  }

  // Utility method to get default dimensions locally (fallback)
  getDefaultDimensions(plotSize: PlotSize) {
    const dimensions = {
      [PlotSize.ADULT]: { length: 2.5, width: 1.2, depth: 2.0 },
      [PlotSize.CHILD]: { length: 1.8, width: 0.8, depth: 1.5 }
    };
    return dimensions[plotSize];
  }

  // Generate plot numbers automatically
  generatePlotNumbers(section: string, row: string, count: number, startNumber: number = 1) {
    const plots = [];
    for (let i = 0; i < count; i++) {
      plots.push({
        section,
        row,
        plotNumber: `${section}-${row}-${String(startNumber + i).padStart(3, '0')}`,
        position: i,
      });
    }
    return plots;
  }

  // Validate plot data
  validatePlotData(plotData: PlotFormData): string[] {
    const errors: string[] = [];

    if (!plotData.section?.trim()) {
      errors.push('Section is required');
    }

    if (!plotData.row?.trim()) {
      errors.push('Row is required');
    }

    if (!plotData.plotNumber?.trim()) {
      errors.push('Plot number is required');
    }

    if (!plotData.plotSize) {
      errors.push('Plot size is required');
    }

    if (plotData.dimensions) {
      if (plotData.dimensions.length <= 0) {
        errors.push('Length must be greater than 0');
      }
      if (plotData.dimensions.width <= 0) {
        errors.push('Width must be greater than 0');
      }
      if (plotData.dimensions.depth <= 0) {
        errors.push('Depth must be greater than 0');
      }
    }

    return errors;
  }

  // Filter plots based on criteria
  filterPlots(plots: Plot[], filters: {
    section?: string;
    row?: string;
    plotSize?: PlotSize;
    isAvailable?: boolean;
    status?: string;
  }): Plot[] {
    return plots.filter(plot => {
      if (filters.section && plot.section !== filters.section) return false;
      if (filters.row && plot.row !== filters.row) return false;
      if (filters.plotSize && plot.plotSize !== filters.plotSize) return false;
      if (filters.isAvailable !== undefined && plot.isAvailable !== filters.isAvailable) return false;
      if (filters.status && plot.status !== filters.status) return false;
      return true;
    });
  }

  // Group plots by section
  groupPlotsBySection(plots: Plot[]): Record<string, Plot[]> {
    return plots.reduce((acc, plot) => {
      const section = plot.section || 'Unknown';
      if (!acc[section]) {
        acc[section] = [];
      }
      acc[section].push(plot);
      return acc;
    }, {} as Record<string, Plot[]>);
  }

  // Group plots by row within a section
  groupPlotsByRow(plots: Plot[]): Record<string, Record<string, Plot[]>> {
    const groupedBySection = this.groupPlotsBySection(plots);
    const result: Record<string, Record<string, Plot[]>> = {};

    Object.entries(groupedBySection).forEach(([section, sectionPlots]) => {
      result[section] = sectionPlots.reduce((acc, plot) => {
        const row = plot.row || 'Unknown';
        if (!acc[row]) {
          acc[row] = [];
        }
        acc[row].push(plot);
        return acc;
      }, {} as Record<string, Plot[]>);
    });

    return result;
  }

  // Calculate plot statistics
  calculatePlotStatistics(plots: Plot[]): PlotSummary {
    const totalPlots = plots.length;
    const availablePlots = plots.filter(p => p.isAvailable).length;
    const occupiedPlots = plots.filter(p => p.status === 'OCCUPIED').length;
    const reservedPlots = plots.filter(p => p.status === 'RESERVED').length;
    const maintenancePlots = plots.filter(p => p.status === 'MAINTENANCE').length;
    const adultPlots = plots.filter(p => p.plotSize === PlotSize.ADULT).length;
    const childPlots = plots.filter(p => p.plotSize === PlotSize.CHILD).length;
    const sections = [...new Set(plots.map(p => p.section).filter(Boolean) as string[])];
    const rows = [...new Set(plots.map(p => p.row).filter(Boolean) as string[])];

    return {
      totalPlots,
      availablePlots,
      occupiedPlots,
      reservedPlots,
      maintenancePlots,
      adultPlots,
      childPlots,
      sections,
      rows,
    };
  }
}

export const plotService = new PlotService();
export default plotService;